import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

export type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  isDark: boolean;
  isLight: boolean;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  getTheme: () => Theme;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

// Utility class for centralized theme management
export class ThemeManager {
  private static instance: ThemeManager;
  private theme: Theme = 'light';
  private listeners: Array<(theme: Theme) => void> = [];

  private constructor() {
    this.initializeTheme();
  }

  public static getInstance(): ThemeManager {
    if (!ThemeManager.instance) {
      ThemeManager.instance = new ThemeManager();
    }
    return ThemeManager.instance;
  }

  private initializeTheme(): void {
    // Check localStorage first, then system preference
    const savedTheme = localStorage.getItem('theme') as Theme;
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      this.theme = savedTheme;
    } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      this.theme = 'dark';
    } else {
      this.theme = 'light';
    }
    this.applyTheme();
  }

  public getTheme(): Theme {
    return this.theme;
  }

  public setTheme(theme: Theme): void {
    if (this.theme !== theme) {
      this.theme = theme;
      this.applyTheme();
      this.notifyListeners();
    }
  }

  public toggleTheme(): void {
    this.setTheme(this.theme === 'light' ? 'dark' : 'light');
  }

  public isDark(): boolean {
    return this.theme === 'dark';
  }

  public isLight(): boolean {
    return this.theme === 'light';
  }

  private applyTheme(): void {
    document.documentElement.setAttribute('data-theme', this.theme);
    localStorage.setItem('theme', this.theme);
    
    // Apply theme class for additional styling if needed
    document.body.className = document.body.className.replace(/\b(light|dark)-theme\b/g, '');
    document.body.classList.add(`${this.theme}-theme`);
  }

  public subscribe(listener: (theme: Theme) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.theme));
  }

  // Utility method to get CSS custom property value
  public getCSSVariable(variable: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
  }

  // Utility method to check if current theme matches
  public matchesTheme(theme: Theme): boolean {
    return this.theme === theme;
  }
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeManager] = useState(() => ThemeManager.getInstance());
  const [theme, setThemeState] = useState<Theme>(themeManager.getTheme());

  useEffect(() => {
    const unsubscribe = themeManager.subscribe(setThemeState);
    return unsubscribe;
  }, [themeManager]);

  const toggleTheme = useCallback(() => {
    themeManager.toggleTheme();
  }, [themeManager]);

  const setTheme = useCallback((newTheme: Theme) => {
    themeManager.setTheme(newTheme);
  }, [themeManager]);

  const getTheme = useCallback(() => {
    return themeManager.getTheme();
  }, [themeManager]);

  const value: ThemeContextType = {
    theme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    toggleTheme,
    setTheme,
    getTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Export the singleton instance for direct access
export const themeManager = ThemeManager.getInstance();

// Utility hooks for common theme operations
export const useIsDark = () => {
  const { isDark } = useTheme();
  return isDark;
};

export const useIsLight = () => {
  const { isLight } = useTheme();
  return isLight;
};

export const useThemeValue = () => {
  const { theme } = useTheme();
  return theme;
};